import { supabase } from './supabase'

const PHOTO_MAX_LONG_EDGE = 2000 // px
const PHOTO_JPEG_QUALITY = 0.85

/**
 * Resize an image File client-side before upload. Returns a JPEG Blob.
 * Skips resize for non-image inputs (returns original file).
 */
export async function resizeImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file
  const dataUrl = await readAsDataURL(file)
  const img = await loadImage(dataUrl)
  const { naturalWidth: w, naturalHeight: h } = img
  const maxEdge = Math.max(w, h)
  if (maxEdge <= PHOTO_MAX_LONG_EDGE) {
    // Already small enough — still re-encode as JPEG for consistency
    return imageToBlob(img, w, h)
  }
  const scale = PHOTO_MAX_LONG_EDGE / maxEdge
  return imageToBlob(img, Math.round(w * scale), Math.round(h * scale))
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = src
  })
}

function imageToBlob(img: HTMLImageElement, w: number, h: number): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, w, h)
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas toBlob failed'))),
      'image/jpeg',
      PHOTO_JPEG_QUALITY,
    )
  })
}

// Map of accepted file extensions to their canonical MIME type. Anything
// not on this list gets a generic application/octet-stream so a browser
// can't be tricked into rendering the file inline.
const CONTENT_TYPE_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  stl: 'model/stl',
  '3mf': 'model/3mf',
  gcode: 'text/plain',
  obj: 'model/obj',
  step: 'model/step',
}

function sanitizeFileName(fileName: string): string {
  // Collapse all but the final '.' into underscores. `evil.html.stl` would
  // otherwise survive sanitization and could (combined with a wrong MIME)
  // get served inline. Result: `evil_html.stl`.
  const lastDot = fileName.lastIndexOf('.')
  const base = lastDot > 0 ? fileName.slice(0, lastDot) : fileName
  const ext = lastDot > 0 ? fileName.slice(lastDot) : ''
  return (base.replace(/\./g, '_') + ext).replace(/[^a-zA-Z0-9._-]/g, '_')
}

function inferContentType(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot < 0) return 'application/octet-stream'
  const ext = fileName.slice(lastDot + 1).toLowerCase()
  return CONTENT_TYPE_BY_EXT[ext] ?? 'application/octet-stream'
}

/**
 * Upload a file to Supabase Storage. Returns the storage path (relative to bucket).
 * Path convention: {user_id}/{product_id}/{timestamp}_{filename}
 */
export async function uploadToBucket(
  bucket: 'photos' | 'model-files',
  productId: string,
  fileName: string,
  blob: Blob,
): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('not authenticated')

  const sanitized = sanitizeFileName(fileName)
  const path = `${user.id}/${productId}/${Date.now()}_${sanitized}`
  const { error } = await supabase.storage.from(bucket).upload(path, blob, {
    cacheControl: '3600',
    upsert: false,
    // Lock the served Content-Type to what the extension implies so a
    // sneaky double-extension upload can't get re-served as something
    // executable. Defaults to octet-stream for anything unrecognized.
    contentType: inferContentType(sanitized),
  })
  if (error) throw error
  return path
}

/**
 * Generate a temporary signed URL to read a stored file.
 * Default expiry: 1 hour. For share links, pass a longer expiresIn.
 */
export async function signedUrl(
  bucket: 'photos' | 'model-files',
  path: string,
  expiresIn = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn)
  if (error) throw error
  return data.signedUrl
}

export async function deleteFromBucket(
  bucket: 'photos' | 'model-files',
  path: string,
): Promise<void> {
  const { error } = await supabase.storage.from(bucket).remove([path])
  if (error) throw error
}
